import React, { useState, useEffect, useCallback } from 'react';
import { balancesApi, businessLinesApi, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Wallet, FloppyDisk } from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const BalancesPage = () => {
  const [users, setUsers] = useState([]);
  const [businessLines, setBusinessLines] = useState([]);
  const [selectedBL, setSelectedBL] = useState('all');
  const [loading, setLoading] = useState(true);
  const [changes, setChanges] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = selectedBL !== 'all' ? { business_line_id: selectedBL } : {};
      const [usersRes, blRes] = await Promise.all([
        balancesApi.getAll(params),
        businessLinesApi.getAll(),
      ]);
      setUsers(usersRes.data);
      setBusinessLines(blRes.data);
    } catch (e) { console.error(e); toast.error('Errore nel caricamento'); } finally { setLoading(false); }
  }, [selectedBL]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleChange = (userId, field, value) => {
    setChanges(prev => ({
      ...prev,
      [userId]: { ...prev[userId], [field]: parseFloat(value) || 0 }
    }));
  };

  const handleSave = async () => {
    const updates = Object.entries(changes).map(([user_id, vals]) => ({
      user_id,
      leave_hours: vals.leave_balance_hours,
      rol_hours: vals.rol_balance_hours,
    })).filter(u => u.leave_hours !== undefined || u.rol_hours !== undefined);

    if (updates.length === 0) {
      toast.info('Nessuna modifica da salvare');
      return;
    }

    setSaving(true);
    try {
      const { data } = await balancesApi.bulkUpdate(updates);
      toast.success(`Aggiornati ${data.total_updated} saldi`);
      setChanges({});
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const getValue = (user, field) => {
    if (changes[user.id]?.[field] !== undefined) return changes[user.id][field];
    return user[field] || 0;
  };

  const hasChanges = Object.keys(changes).length > 0;

  return (
    <div data-testid="balances-page">
      <Toaster position="top-right" richColors />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 md:mb-6 gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#0A0A0A]">Gestione Saldi</h1>
          <p className="text-[#52525B] mt-1">Aggiorna in modo massivo i saldi ferie e ROL dei dipendenti (in ore)</p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={selectedBL} onValueChange={setSelectedBL}>
            <SelectTrigger className="w-56 rounded-none border-[#E4E4E7]" data-testid="bl-filter">
              <SelectValue placeholder="Filtra per linea di business" />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all" className="rounded-none">Tutti</SelectItem>
              {businessLines.map(bl => (
                <SelectItem key={bl.id} value={bl.id} className="rounded-none">{bl.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {hasChanges && (
            <Button onClick={handleSave} disabled={saving}
              className="bg-[#002FA7] text-white px-6 py-2.5 font-medium hover:bg-[#00278A] rounded-none" data-testid="save-balances-btn">
              <FloppyDisk size={20} weight="bold" className="mr-2" />
              {saving ? 'Salvataggio...' : `Salva (${Object.keys(changes).length})`}
            </Button>
          )}
        </div>
      </div>

      <div className="bg-[#FAFAFA] border border-[#E4E4E7] p-4 mb-6">
        <p className="text-sm text-[#52525B]">
          <b>Info:</b> 1 giornata lavorativa = {8} ore. Inserire il saldo totale in ore.
          I permessi ROL possono essere utilizzati anche per frazioni di giornata (1-2 ore).
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#0A0A0A] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : users.length === 0 ? (
        <div className="border border-[#E4E4E7] p-12 text-center">
          <Wallet size={48} className="mx-auto text-[#E4E4E7] mb-4" />
          <h3 className="font-heading font-bold text-lg mb-2">Nessun dipendente trovato</h3>
          <p className="text-[#52525B]">Seleziona una linea di business o assegna utenti ai team.</p>
        </div>
      ) : (
        <div className="border border-[#E4E4E7] overflow-x-auto">
          <table className="w-full data-table min-w-[700px]">
            <thead>
              <tr className="border-b-2 border-[#0A0A0A] bg-white">
                <th className="py-4 px-4 text-left">Dipendente</th>
                <th className="py-4 px-4 text-left">Email</th>
                <th className="py-4 px-4 text-left">Ruolo</th>
                <th className="py-4 px-4 text-center">Ferie (ore)</th>
                <th className="py-4 px-4 text-center">ROL (ore)</th>
                <th className="py-4 px-4 text-center">Ferie (giorni)</th>
                <th className="py-4 px-4 text-center">ROL (giorni)</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const leaveHours = getValue(u, 'leave_balance_hours');
                const rolHours = getValue(u, 'rol_balance_hours');
                const isChanged = changes[u.id] !== undefined;
                return (
                  <tr key={u.id} className={`border-b border-[#E4E4E7] hover:bg-[#F4F4F5] ${isChanged ? 'bg-[#002FA7]/5' : ''}`}
                    data-testid={`balance-row-${u.id}`}>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-[#0A0A0A] flex items-center justify-center text-white font-bold text-xs">
                          {u.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                        </div>
                        <span className="font-medium">{u.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-[#52525B]">{u.email}</td>
                    <td className="py-3 px-4 text-xs uppercase font-bold text-[#52525B]">{u.role?.replace('_', ' ')}</td>
                    <td className="py-3 px-4">
                      <Input type="number" step="0.5" value={leaveHours}
                        onChange={e => handleChange(u.id, 'leave_balance_hours', e.target.value)}
                        className="w-24 mx-auto text-center rounded-none border-[#E4E4E7] text-sm"
                        data-testid={`leave-input-${u.id}`} />
                    </td>
                    <td className="py-3 px-4">
                      <Input type="number" step="0.5" value={rolHours}
                        onChange={e => handleChange(u.id, 'rol_balance_hours', e.target.value)}
                        className="w-24 mx-auto text-center rounded-none border-[#E4E4E7] text-sm"
                        data-testid={`rol-input-${u.id}`} />
                    </td>
                    <td className="py-3 px-4 text-center font-mono text-sm text-[#52525B]">
                      {(leaveHours / 8).toFixed(1)} gg
                    </td>
                    <td className="py-3 px-4 text-center font-mono text-sm text-[#52525B]">
                      {(rolHours / 8).toFixed(1)} gg
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BalancesPage;
